<template>
    <div class="box m-b-0 box-modal">
        <div class="box-header">
            <h3>{{ title }}</h3>
            <small v-if="description">{{ description }}</small>
        </div>
        <div class="box-tool">
            <ul class="nav">
                <li class="nav-item inline icon-with-desc">
                    <a class="nav-link text-muted" @click="saveAndExit()">
                        <i class="material-icons md-18">done</i>
                        <span class="icon-desc">CTRL+S</span>
                    </a>
                </li>
                <li class="nav-item inline icon-with-desc">
                    <a class="nav-link text-muted" @click="$emit('close')">
                        <i class="material-icons md-18">clear</i>
                        <span class="icon-desc">ESC</span>
                    </a>
                </li>
            </ul>
        </div>

        <div class="box-body number-selector-body">
            <div class="number-selector-row">
                <div
                    v-for="(field, idx) in normalizedFields"
                    :key="'f_'+idx"
                    class="number-selector-field"
                    :class="{ 'is-active': activeIndex === idx }"
                    @click="activeIndex = idx">

                    <div class="number-selector-label">{{ field.label }}</div>

                    <button
                        type="button"
                        class="number-selector-btn"
                        :disabled="values[idx] <= field.min"
                        @click.stop="bump(idx, -field.step)">
                        <i class="material-icons">keyboard_arrow_up</i>
                    </button>

                    <div class="number-selector-display">
                        <div
                            v-for="offset in [-2, -1]"
                            :key="'p'+offset"
                            class="number-selector-neighbour"
                            :class="{ 'is-hidden': values[idx] + offset < field.min }"
                            @click.stop="setValue(idx, values[idx] + offset)">
                            {{ formatValue(field, values[idx] + offset) }}
                        </div>

                        <input
                            ref="inputs"
                            type="number"
                            class="number-selector-input"
                            :min="field.min"
                            :max="field.max"
                            :step="field.step"
                            v-model.number="values[idx]"
                            @focus="activeIndex = idx"
                            @input="clamp(idx)">

                        <div
                            v-for="offset in [1, 2]"
                            :key="'n'+offset"
                            class="number-selector-neighbour"
                            :class="{ 'is-hidden': values[idx] + offset > field.max }"
                            @click.stop="setValue(idx, values[idx] + offset)">
                            {{ formatValue(field, values[idx] + offset) }}
                        </div>
                    </div>

                    <button
                        type="button"
                        class="number-selector-btn"
                        :disabled="values[idx] >= field.max"
                        @click.stop="bump(idx, field.step)">
                        <i class="material-icons">keyboard_arrow_down</i>
                    </button>

                    <div class="number-selector-range">{{ field.min }} – {{ field.max }}</div>
                </div>
            </div>

            <div v-if="errorMessage" class="number-selector-error">
                <i class="material-icons md-16">error_outline</i> {{ errorMessage }}
            </div>
        </div>

        <div class="box-footer clearfix">
            <button class="btn white none-radius btn-sm" @click="$emit('close')">Anuluj</button>
            <button
                class="pull-right btn info none-radius btn-sm"
                type="button"
                style="height: 32px;"
                :disabled="!!errorMessage"
                @click="saveAndExit()">
                Zapisz
            </button>
        </div>
    </div>
</template>

<script>

/**
 * Okno modalne do wyboru liczby (lub kilku liczb) z zadanego zakresu.
 *
 * Tryb pojedynczy:
 *   this.$modal2.show(GenericNumberSelectorModal, {
 *       title: "Wybierz rok",
 *       min: 2020,
 *       max: 2026,
 *       value: 2024
 *   });
 *   // => zwraca pojedynczą liczbę, np. 2024
 *
 * Tryb wielu pól (np. data urodzenia):
 *   this.$modal2.show(GenericNumberSelectorModal, {
 *       title: "Data urodzenia",
 *       fields: [
 *           { key: "day",   label: "Dzień",   min: 1,    max: 31,   value: 15 },
 *           { key: "month", label: "Miesiąc", min: 1,    max: 12,   value: 6  },
 *           { key: "year",  label: "Rok",     min: 1900, max: 2026, value: 1990 }
 *       ]
 *   });
 *   // => zwraca obiekt { day: 15, month: 6, year: 1990 }
 *   // (jeśli nie podano `key`, zwracana jest tablica liczb)
 */
export default {
    name: 'GenericNumberSelectorModal',
    props: {
        title: {
            type: String,
            default: 'Wybierz liczbę'
        },
        description: {
            type: String,
            default: ''
        },
        // tryb pojedynczy
        min: { type: Number, default: 0 },
        max: { type: Number, default: 100 },
        step: { type: Number, default: 1 },
        value: { type: Number, default: null },
        label: { type: String, default: '' },
        // tryb wielu pól
        fields: {
            type: Array,
            default: null
        }
    },
    data() {
        return {
            values: [],
            activeIndex: 0
        };
    },
    computed: {

        /**
         * Zwraca znormalizowaną listę pól (zawsze tablica obiektów z min/max/step/label/key).
         */
        normalizedFields() {
            if (Array.isArray(this.fields) && this.fields.length) {
                return this.fields.map((f, i) => ({
                    key: f.key || null,
                    label: f.label || '',
                    min: Number.isFinite(f.min) ? f.min : 0,
                    max: Number.isFinite(f.max) ? f.max : 100,
                    step: Number.isFinite(f.step) && f.step > 0 ? f.step : 1,
                    value: Number.isFinite(f.value) ? f.value : (Number.isFinite(f.min) ? f.min : 0),
                    pad: Number.isFinite(f.pad) ? f.pad : 0
                }));
            }
            return [{
                key: null,
                label: this.label,
                min: this.min,
                max: this.max,
                step: this.step > 0 ? this.step : 1,
                value: Number.isFinite(this.value) ? this.value : this.min,
                pad: 0
            }];
        },

        isMultiMode() {
            return Array.isArray(this.fields) && this.fields.length > 0;
        },

        errorMessage() {
            for (let i = 0; i < this.normalizedFields.length; i++) {
                const f = this.normalizedFields[i];
                const v = this.values[i];
                if (!Number.isFinite(v)) return `Pole "${f.label || (i + 1)}" musi być liczbą`;
                if (v < f.min || v > f.max) return `Pole "${f.label || (i + 1)}" musi być z zakresu ${f.min}–${f.max}`;
            }
            return null;
        }
    },
    created() {
        this.values = this.normalizedFields.map(f => f.value);
        window.addEventListener('keydown', this.handleKeyDown);
    },
    mounted() {
        this.$nextTick(() => {
            const inputs = this.$refs.inputs;
            if (inputs && inputs.length) {
                inputs[0].focus();
                inputs[0].select();
            }
        });
    },
    destroyed() {
        window.removeEventListener('keydown', this.handleKeyDown);
    },
    methods: {

        /**
         * Formatuje wartość uwzględniając ewentualne zerowe wyrównanie (pad).
         */
        formatValue(field, v) {
            if (!Number.isFinite(v)) return '';
            if (field.pad && field.pad > 0) {
                const s = String(Math.abs(v));
                const padded = s.padStart(field.pad, '0');
                return v < 0 ? '-' + padded : padded;
            }
            return String(v);
        },

        /**
         * Zmienia wartość pola o zadany krok, ograniczając ją do zakresu.
         */
        bump(idx, delta) {
            const f = this.normalizedFields[idx];
            const next = Math.min(f.max, Math.max(f.min, (this.values[idx] || 0) + delta));
            this.$set(this.values, idx, next);
        },

        /**
         * Ustawia wartość pola, ograniczając ją do zakresu.
         */
        setValue(idx, v) {
            const f = this.normalizedFields[idx];
            if (v < f.min || v > f.max) return;
            this.$set(this.values, idx, v);
        },

        /**
         * Przycina wartość pola do dozwolonego zakresu po edycji ręcznej.
         */
        clamp(idx) {
            const f = this.normalizedFields[idx];
            let v = this.values[idx];
            if (!Number.isFinite(v)) return;
            if (v < f.min) v = f.min;
            if (v > f.max) v = f.max;
            this.$set(this.values, idx, v);
        },

        /**
         * Obsługa skrótów klawiaturowych (ESC, CTRL+S, strzałki góra/dół, lewo/prawo).
         */
        handleKeyDown(e) {
            if (e.keyCode === 27) { // ESC
                e.preventDefault();
                this.$emit('close');
                return;
            }
            if (e.ctrlKey && e.keyCode === 83) { // CTRL+S
                e.preventDefault();
                this.saveAndExit();
                return;
            }
            const idx = this.activeIndex;
            const f = this.normalizedFields[idx];
            if (!f) return;

            if (e.keyCode === 38) { // strzałka góra
                e.preventDefault();
                this.bump(idx, -f.step);
            } else if (e.keyCode === 40) { // strzałka dół
                e.preventDefault();
                this.bump(idx, f.step);
            } else if (e.keyCode === 37 && this.normalizedFields.length > 1) { // strzałka lewo
                if (e.target && e.target.tagName === 'INPUT') return;
                e.preventDefault();
                this.activeIndex = Math.max(0, idx - 1);
            } else if (e.keyCode === 39 && this.normalizedFields.length > 1) { // strzałka prawo
                if (e.target && e.target.tagName === 'INPUT') return;
                e.preventDefault();
                this.activeIndex = Math.min(this.normalizedFields.length - 1, idx + 1);
            }
        },

        /**
         * Zamyka okno zwracając wybrane wartości.
         * Zwraca pojedynczą liczbę w trybie single, obiekt jeśli pola mają `key`,
         * w przeciwnym wypadku tablicę liczb.
         */
        saveAndExit() {
            if (this.errorMessage) {
                this.$h && this.$h.errorNotif && this.$h.errorNotif(this.errorMessage);
                return;
            }
            if (!this.isMultiMode) {
                this.$emit('close', this.values[0]);
                return;
            }
            const hasKeys = this.normalizedFields.every(f => !!f.key);
            if (hasKeys) {
                const out = {};
                this.normalizedFields.forEach((f, i) => { out[f.key] = this.values[i]; });
                this.$emit('close', out);
            } else {
                this.$emit('close', this.values.slice());
            }
        }
    }
};
</script>

<style scoped>
.number-selector-body {
    padding: 24px 16px;
}

.number-selector-row {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 12px;
}

.number-selector-field {
    flex: 1 1 110px;
    min-width: 110px;
    max-width: 180px;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 12px 8px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    background: var(--dm-surface, #fafbfc);
    transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
    cursor: pointer;
    user-select: none;
}

.number-selector-field.is-active {
    border-color: #2196f3;
    background: var(--dm-surface, #fff);
    box-shadow: 0 2px 8px rgba(33, 150, 243, 0.12);
}

.number-selector-label {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--dm-text-muted, #6b7280);
    margin-bottom: 8px;
    min-height: 16px;
}

.number-selector-btn {
    width: 36px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    color: var(--dm-text-muted, #6b7280);
    cursor: pointer;
    border-radius: 6px;
    transition: background 0.15s, color 0.15s;
}

.number-selector-btn:hover:not(:disabled) {
    background: #eef2f7;
    color: #2196f3;
}

.number-selector-btn:disabled {
    opacity: 0.25;
    cursor: not-allowed;
}

.number-selector-display {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 4px 0;
    width: 100%;
}

.number-selector-neighbour {
    font-size: 14px;
    color: var(--dm-text-muted, #9ca3af);
    line-height: 1.6;
    cursor: pointer;
    transition: color 0.15s;
    font-variant-numeric: tabular-nums;
}

.number-selector-neighbour:hover {
    color: #2196f3;
}

.number-selector-neighbour.is-hidden {
    visibility: hidden;
}

.number-selector-input {
    width: 100%;
    text-align: center;
    font-size: 28px;
    font-weight: 600;
    color: #1f2937;
    border: none;
    border-bottom: 2px solid #e5e7eb;
    background: transparent;
    padding: 4px 0;
    margin: 4px 0;
    outline: none;
    font-variant-numeric: tabular-nums;
    -moz-appearance: textfield;
}

.number-selector-input::-webkit-outer-spin-button,
.number-selector-input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

.number-selector-input:focus {
    border-bottom-color: #2196f3;
}

.number-selector-range {
    font-size: 11px;
    color: var(--dm-text-muted, #9ca3af);
    margin-top: 6px;
    font-variant-numeric: tabular-nums;
}

.number-selector-error {
    margin-top: 16px;
    padding: 8px 12px;
    border-radius: 6px;
    background: #fef2f2;
    color: #b91c1c;
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 6px;
    justify-content: center;
}
</style>
